import { useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Sparkles } from "lucide-react";

import { Button } from "@/ui/components/Button";
import { Card, CardContent } from "@/ui/components/Card";
import { Field } from "@/ui/components/Field";
import { Input } from "@/ui/components/Input";
import { Textarea } from "@/ui/components/Textarea";
import { RadioGroup } from "@/ui/components/RadioGroup";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/components/Select";
import { Slider } from "@/ui/components/Slider";

import { api, type WizardConfig } from "@/lib/api";
import { canAdvanceFromIntro, type Punchout } from "@/lib/punchout";

import { ProgressBar } from "./ProgressBar";
import { RadioCard } from "./RadioCard";
import { ToggleRow } from "./ToggleRow";
import {
  BUSINESS_MODEL_OPTIONS,
  CONTRACT_MODEL_OPTIONS,
  CORPORATE_OPTIONS,
  INITIAL_CONFIG,
  MARKUP_BASIS_OPTIONS,
  MARKUP_KIND_OPTIONS,
  PAS_OPTIONS,
  STEP_PHASE_LABEL,
  SYNC_HELP,
  syncLabelFor,
  type StepId,
  activeSteps,
  corporateLabel,
  corporateUsesEmailIngestion,
  effectiveBillRate,
  effectiveMarkupPct,
  isUniversalFlat,
  isUniversalPct,
  showsMarkup,
  stepBlockedReason,
  syncOptionsFor,
} from "./state";

interface WizardProps {
  punchout: Punchout;
  setPunchout: (p: Punchout) => void;
}

export function Wizard({ punchout, setPunchout }: WizardProps) {
  const navigate = useNavigate();
  const [cfg, setCfg] = useState<WizardConfig>(INITIAL_CONFIG);
  const [stepIdx, setStepIdx] = useState(0);

  const steps = useMemo(() => activeSteps(cfg), [cfg]);
  const safeIdx = Math.min(stepIdx, steps.length - 1);
  const currentStep: StepId = steps[safeIdx] ?? steps[0]!;

  const update = <K extends keyof WizardConfig>(key: K, value: WizardConfig[K]) =>
    setCfg((prev) => ({ ...prev, [key]: value }));

  const updatePunchout = <K extends keyof Punchout>(key: K, value: Punchout[K]) =>
    setPunchout({ ...punchout, [key]: value });

  const submit = useMutation({
    mutationFn: () => api.createSubmission(cfg, punchout),
    onSuccess: (s) => navigate(`/thanks/${s.id}`),
  });

  // Per-step gate for Continue. Intro has its own rule; every other
  // step delegates to stepBlockedReason which returns a short hint
  // string when a required selection is missing.
  const blockReason: string | null =
    currentStep === "intro"
      ? (canAdvanceFromIntro(punchout) ? null : "")  // intro hint shown inline
      : stepBlockedReason(currentStep, cfg);
  const blocked = blockReason !== null;

  const onBack = () => setStepIdx((i) => Math.max(0, i - 1));
  const onNext = () => {
    if (blocked) return;
    if (safeIdx < steps.length - 1) setStepIdx((i) => i + 1);
    else submit.mutate();
  };

  return (
    <div className="mx-auto max-w-3xl">
      <Card>
        <CardContent className="p-5 sm:p-8">
          <ProgressBar
            current={safeIdx}
            total={steps.length}
            phaseLabel={STEP_PHASE_LABEL[currentStep]}
          />

          <div className="py-6">
            {currentStep === "intro" && (
              <IntroStep punchout={punchout} updatePunchout={updatePunchout} />
            )}
            {currentStep === "business-model" && <BusinessModelStep cfg={cfg} update={update} />}
            {currentStep === "systems" && <SystemsStep cfg={cfg} update={update} />}
            {currentStep === "ap-ar-routing" && <ApArRoutingStep cfg={cfg} update={update} />}
            {currentStep === "pas-sync" && <PasSyncStep cfg={cfg} update={update} />}
            {currentStep === "contract-model" && <ContractModelStep cfg={cfg} update={update} />}
            {currentStep === "markup-application" && <MarkupApplicationStep cfg={cfg} update={update} />}
            {currentStep === "shop-rate" && <ShopRateStep cfg={cfg} update={update} />}
            {currentStep === "bill-rate" && <BillRateStep cfg={cfg} update={update} />}
            {currentStep === "ar-branding" && <ArBrandingStep cfg={cfg} update={update} />}
            {currentStep === "additional-notes" && <AdditionalNotesStep cfg={cfg} update={update} />}
            {currentStep === "preview" && <PreviewStep cfg={cfg} />}
            {currentStep === "review" && <ReviewStep cfg={cfg} />}
          </div>

          {submit.isError && (
            <p className="mb-4 text-sm text-destructive">
              Couldn't save your responses. {(submit.error as Error)?.message}
            </p>
          )}

          <div className="border-t border-border pt-5">
            <div className="flex items-center justify-between">
              <Button variant="ghost" onClick={onBack} disabled={safeIdx === 0 || submit.isPending}>
                <ArrowLeft /> Back
              </Button>
              <Button
                onClick={onNext}
                disabled={submit.isPending || blocked}
                size="lg"
              >
                {safeIdx === steps.length - 1 ? (
                  <>
                    <Sparkles /> {submit.isPending ? "Submitting…" : "Submit responses"}
                  </>
                ) : (
                  <>Continue <ArrowRight /></>
                )}
              </Button>
            </div>
            {blocked && blockReason && (
              <p className="mt-3 text-right text-xs text-muted-foreground">
                {blockReason}
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// --- Helpers ----------------------------------------------------------

interface StepProps {
  cfg: WizardConfig;
  update: <K extends keyof WizardConfig>(key: K, value: WizardConfig[K]) => void;
}

function StepHeader({ title, lede }: { title: string; lede: string }) {
  return (
    <div className="mb-5">
      <h2 className="font-display text-xl font-semibold leading-tight">{title}</h2>
      <p className="mt-1 text-sm text-muted-foreground">{lede}</p>
    </div>
  );
}

// --- Step 0: Intro ---------------------------------------------------

interface IntroStepProps {
  punchout: Punchout;
  updatePunchout: <K extends keyof Punchout>(key: K, value: Punchout[K]) => void;
}

function IntroStep({ punchout, updatePunchout }: IntroStepProps) {
  const firstName = punchout.user_name.trim().split(" ")[0];
  // We decide whether to ask for company ONCE at first mount based on
  // whether the URL provided one. Re-deriving every render would unmount
  // the field as soon as the user types their first character.
  const [askCompany] = useState(() => !punchout.company.trim());
  // Email is conditional on the checkbox, which is independent of the
  // email field's own value, so deriving every render is fine.
  const askEmail = punchout.openToFollowup;

  return (
    <>
      <StepHeader
        title={`Thanks for taking the TrueCost billing survey${firstName ? `, ${firstName}` : ""}!`}
        lede="We're excited to make sure Meld can meet all your billing needs. This survey is best taken by your billing manager or someone who has a good understanding of your billing process. Feel free to pass this along to the right person."
      />
      <div className="space-y-4">
        <Field label="Your name" required>
          <Input
            value={punchout.user_name}
            onChange={(e) => updatePunchout("user_name", e.target.value)}
            placeholder="Erin Karam"
            autoFocus={!punchout.user_name}
          />
        </Field>

        {askCompany && (
          <Field label="Company name" required>
            <Input
              value={punchout.company}
              onChange={(e) => updatePunchout("company", e.target.value)}
              placeholder="Acme Property Management"
            />
          </Field>
        )}

        <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-border bg-card p-4">
          <input
            type="checkbox"
            checked={punchout.openToFollowup}
            onChange={(e) => updatePunchout("openToFollowup", e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-border accent-primary"
          />
          <div>
            <div className="font-medium">Open to a follow-up conversation</div>
            <div className="text-sm text-muted-foreground">
              We'd love to dig deeper on your answers — say yes if you're open to a 20-minute chat.
            </div>
          </div>
        </label>

        {askEmail && (
          <Field label="Your work email" hint="so we can reach you" required>
            <Input
              type="email"
              value={punchout.user_email}
              onChange={(e) => updatePunchout("user_email", e.target.value)}
              placeholder="you@example.com"
            />
          </Field>
        )}
      </div>
    </>
  );
}

// --- Step 1: Business Model -------------------------------------------

function BusinessModelStep({ cfg, update }: StepProps) {
  return (
    <>
      <StepHeader
        title="What's your business model?"
        lede="Helps us understand which document flows apply to your portfolio (AP, AR, or both)."
      />
      <RadioGroup
        value={cfg.businessModel ?? ""}
        onValueChange={(v) => update("businessModel", v as WizardConfig["businessModel"])}
        className="grid gap-3"
      >
        {BUSINESS_MODEL_OPTIONS.map((opt) => (
          <RadioCard key={opt.value} value={opt.value} title={opt.title} help={opt.help} />
        ))}
      </RadioGroup>
    </>
  );
}

// --- Step 2: Systems --------------------------------------------------

function SystemsStep({ cfg, update }: StepProps) {
  return (
    <>
      <StepHeader
        title="What systems do you use today?"
        lede="Pick your property accounting system (PAS) and corporate accounting system."
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Property accounting system (PAS)">
          <Select
            value={cfg.pas ?? undefined}
            onValueChange={(v) => {
              update("pas", v as WizardConfig["pas"]);
              // Reset the sync-mode selection when PAS changes — avoids
              // carrying over an option that's not valid for the new PAS,
              // and keeps with the no-preselected-default rule.
              update("pmsSyncMode", null);
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select your PAS" />
            </SelectTrigger>
            <SelectContent>
              {PAS_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>

        <Field label="Corporate accounting">
          <Select
            value={cfg.corporate ?? undefined}
            onValueChange={(v) => update("corporate", v as WizardConfig["corporate"])}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select your accounting system" />
            </SelectTrigger>
            <SelectContent>
              {CORPORATE_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
      </div>
      {cfg.corporate === "Other" && (
        <Field label="Which corporate accounting system?" hint="optional" className="mt-4">
          <Input
            value={cfg.corporateOther}
            onChange={(e) => update("corporateOther", e.target.value)}
            placeholder="e.g. NetSuite, Wave, custom in-house"
          />
        </Field>
      )}
      {cfg.corporate === "Same as PAS" && cfg.pas !== "None" && (
        <p className="mt-4 rounded-md bg-secondary px-4 py-3 text-sm">
          Noted — <span className="font-medium">{cfg.pas}</span> is your PAS and your corporate
          accounting system. The next step's document destinations will collapse to a single
          target.
        </p>
      )}
    </>
  );
}

// --- Step 3: AP/AR Routing -------------------------------------------

function ApArRoutingStep({ cfg, update }: StepProps) {
  const noPas = cfg.pas === "None";
  const corpDisabled = cfg.corporate === "Same as PAS"; // hidden when corporate == PAS
  const showAr = cfg.businessModel !== "owned";
  const corpName = corporateLabel(cfg);
  const ingestion = corporateUsesEmailIngestion(cfg);

  const corpLabel = corpDisabled
    ? "Corporate accounting (same as PAS)"
    : `Corporate accounting (${corpName})`;

  return (
    <>
      <StepHeader
        title="Where do you want AP and AR documents to land?"
        lede="Tell us where each PDF should go. Leave both unchecked if you'd want the document kept in TrueCost only."
      />

      <div className="space-y-5">
        <DestinationGroup
          title="Send AP PDF (vendor bill) to:"
          pasLabel={noPas ? "My PAS (none selected)" : `My PAS (${cfg.pas})`}
          pasChecked={cfg.apDestPas}
          pasDisabled={noPas}
          onPasChange={(v) => update("apDestPas", v)}
          corpLabel={corpLabel}
          corpChecked={cfg.apDestCorporate}
          corpDisabled={corpDisabled}
          onCorpChange={(v) => update("apDestCorporate", v)}
          email={cfg.apEmail}
          onEmailChange={(v) => update("apEmail", v)}
          emailPlaceholder={ingestion ? `bills@${corpName.toLowerCase().replace(/\s+/g, "")}.example` : "ap@example.com"}
        />

        {showAr ? (
          <DestinationGroup
            title="Send AR PDF (owner invoice) to:"
            pasLabel={noPas ? "My PAS (none selected)" : `My PAS (${cfg.pas})`}
            pasChecked={cfg.arDestPas}
            pasDisabled={noPas}
            onPasChange={(v) => update("arDestPas", v)}
            corpLabel={corpLabel}
            corpChecked={cfg.arDestCorporate}
            corpDisabled={corpDisabled}
            onCorpChange={(v) => update("arDestCorporate", v)}
            email={cfg.arEmail}
            onEmailChange={(v) => update("arEmail", v)}
            emailPlaceholder={ingestion ? `invoices@${corpName.toLowerCase().replace(/\s+/g, "")}.example` : "ar@example.com"}
          />
        ) : (
          <p className="rounded-md bg-secondary px-4 py-3 text-sm">
            <span className="font-medium">No AR flow.</span> You own all your units, so there are
            no invoices to external owners.
          </p>
        )}
      </div>
    </>
  );
}

// --- Step 4: PAS Data Sync -------------------------------------------

function PasSyncStep({ cfg, update }: StepProps) {
  // If PAS hasn't been picked yet, treat it the same as "None" — there
  // are no sync options to render. The user can go back and select one.
  const noPas = cfg.pas === null || cfg.pas === "None";
  const allowed = cfg.pas ? syncOptionsFor(cfg.pas) : [];
  return (
    <>
      <StepHeader
        title="How should billing data sync to your PAS?"
        lede="Beyond the PDF, the structured billing data also flows into your PAS. Pick how — formats vary by system."
      />
      {noPas ? (
        <p className="rounded-md bg-secondary px-4 py-3 text-sm text-muted-foreground">
          {cfg.pas === null
            ? "Pick a PAS on the previous step to see sync options."
            : "No PAS selected — bills would stay in TrueCost only, with no system push."}
        </p>
      ) : (
        <RadioGroup
          value={cfg.pmsSyncMode ?? ""}
          onValueChange={(v) => update("pmsSyncMode", v as WizardConfig["pmsSyncMode"])}
          className="grid gap-3"
        >
          {cfg.pas && allowed.map((mode) => (
            <RadioCard
              key={mode}
              value={mode}
              title={syncLabelFor(cfg.pas!, mode)}
              help={SYNC_HELP[mode]}
            />
          ))}
        </RadioGroup>
      )}
    </>
  );
}

interface DestinationGroupProps {
  title: string;
  pasLabel: string;
  pasChecked: boolean;
  pasDisabled: boolean;
  onPasChange: (v: boolean) => void;
  corpLabel: string;
  corpChecked: boolean;
  corpDisabled: boolean;
  onCorpChange: (v: boolean) => void;
  email: string;
  onEmailChange: (v: string) => void;
  emailPlaceholder: string;
}

function DestinationGroup({
  title, pasLabel, pasChecked, pasDisabled, onPasChange,
  corpLabel, corpChecked, corpDisabled, onCorpChange,
  email, onEmailChange, emailPlaceholder,
}: DestinationGroupProps) {
  const showEmail = corpChecked && !corpDisabled;
  return (
    <div>
      <div className="mb-2 text-sm font-medium">{title}</div>
      <div className="flex flex-col gap-2 rounded-lg border border-border bg-card p-4">
        <CheckboxLabel
          checked={pasChecked && !pasDisabled}
          disabled={pasDisabled}
          onChange={onPasChange}
        >
          {pasLabel}
        </CheckboxLabel>
        <div className="flex flex-col gap-2 text-sm sm:flex-row sm:items-center sm:gap-3">
          <label
            className={`flex items-center gap-3 ${corpDisabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
          >
            <input
              type="checkbox"
              checked={corpChecked && !corpDisabled}
              disabled={corpDisabled}
              onChange={(e) => onCorpChange(e.target.checked)}
              className="h-4 w-4 rounded border-border accent-primary"
            />
            <span className="sm:whitespace-nowrap">{corpLabel}</span>
          </label>
          {showEmail && (
            <Input
              type="email"
              placeholder={emailPlaceholder}
              value={email}
              onChange={(e) => onEmailChange(e.target.value)}
              className="sm:ml-auto sm:flex-1 sm:min-w-0"
            />
          )}
        </div>
      </div>
    </div>
  );
}

function CheckboxLabel({
  checked, disabled, onChange, children,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
  children: React.ReactNode;
}) {
  return (
    <label
      className={`flex items-center gap-3 text-sm ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-border accent-primary"
      />
      <span>{children}</span>
    </label>
  );
}

// --- Step 4: Contract Model ------------------------------------------

function ContractModelStep({ cfg, update }: StepProps) {
  return (
    <>
      <StepHeader
        title="What's your contract model for managed properties?"
        lede="How do you charge owners today? Pick whichever best matches your current arrangement."
      />
      <RadioGroup
        value={cfg.contractModel ?? ""}
        onValueChange={(v) => update("contractModel", v as WizardConfig["contractModel"])}
        className="grid gap-3"
      >
        {CONTRACT_MODEL_OPTIONS.map((opt) => (
          <RadioCard key={opt.value} value={opt.value} title={opt.title} help={opt.help} />
        ))}
      </RadioGroup>
    </>
  );
}

// --- Step 5: Markup Application --------------------------------------

function MarkupApplicationStep({ cfg, update }: StepProps) {
  const isOther = cfg.markupKind === "other";
  const isUniversal = !isOther && cfg.markupBasis === "universal";
  const isFlat = cfg.markupKind === "flat";
  const isPct = cfg.markupKind === "percentage";

  return (
    <>
      <StepHeader
        title="How do you apply markup today?"
        lede="Two quick questions: what kind of markup, then how it's applied."
      />

      <div className="space-y-6">
        <Field label="What kind of markup?">
          <RadioGroup
            value={cfg.markupKind ?? ""}
            onValueChange={(v) => update("markupKind", v as WizardConfig["markupKind"])}
            className="grid gap-3 sm:grid-cols-3"
          >
            {MARKUP_KIND_OPTIONS.map((opt) => (
              <RadioCard key={opt.value} value={opt.value} title={opt.title} help={opt.help} />
            ))}
          </RadioGroup>
        </Field>

        {!isOther && (
          <Field label="How is it applied?">
            <RadioGroup
              value={cfg.markupBasis ?? ""}
              onValueChange={(v) => update("markupBasis", v as WizardConfig["markupBasis"])}
              className="grid gap-3 sm:grid-cols-2"
            >
              {MARKUP_BASIS_OPTIONS.map((opt) => (
                <RadioCard key={opt.value} value={opt.value} title={opt.title} help={opt.help} />
              ))}
            </RadioGroup>
          </Field>
        )}

        {isOther && (
          <Field label="Describe your markup structure">
            <Textarea
              rows={3}
              value={cfg.markupOther}
              onChange={(e) => update("markupOther", e.target.value)}
              placeholder="e.g. tiered % by job size; flat fee on small jobs, % on larger ones; capped at $X…"
            />
          </Field>
        )}

        {isUniversal && isFlat && (
          <Field label="Typical flat fee per meld">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">$</span>
              <Input
                type="number"
                value={cfg.markupValue}
                onChange={(e) => update("markupValue", parseFloat(e.target.value) || 0)}
                className="w-32"
              />
              <span className="text-sm text-muted-foreground">per meld</span>
            </div>
          </Field>
        )}

        {isUniversal && isPct && (
          <Field label="Typical markup percentage" hint={`${cfg.markupValue.toFixed(0)}%`}>
            <div className="flex items-center gap-4">
              <Slider
                value={[cfg.markupValue]}
                onValueChange={(v) => update("markupValue", v[0] ?? cfg.markupValue)}
                min={0}
                max={50}
                step={1}
                className="flex-1"
              />
              <span className="w-12 text-right tabular-nums text-sm font-medium">
                {cfg.markupValue.toFixed(0)}%
              </span>
            </div>
          </Field>
        )}

        {!isOther && !isUniversal && cfg.markupBasis && (
          <p className="rounded-md bg-secondary px-4 py-3 text-sm text-muted-foreground">
            Variable structures don't have a single default value — we'll record that you charge
            this way and follow up to learn the per-{cfg.markupBasis.replace("by-", "")} rates.
          </p>
        )}
      </div>
    </>
  );
}

// --- Step 6: Shop Rate -----------------------------------------------

function ShopRateStep({ cfg, update }: StepProps) {
  return (
    <>
      <StepHeader
        title="Shop rate"
        lede="What's the hourly cost of your in-house tech labor today?"
      />
      <div className="space-y-4">
        <Field label="Typical shop rate">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">$</span>
            <Input
              type="number"
              value={cfg.shopRate}
              onChange={(e) => update("shopRate", parseFloat(e.target.value) || 0)}
              className="w-32"
            />
            <span className="text-sm text-muted-foreground">per hour</span>
          </div>
        </Field>
        <ToggleRow
          title="Different rates per technician?"
          help="Some PMs use a different shop rate for specific techs (e.g., senior HVAC vs. general handyman)."
          checked={cfg.shopOverrideTech}
          onCheckedChange={(v) => update("shopOverrideTech", v)}
        />
        <ToggleRow
          title="Different rates per service category?"
          help="E.g., HVAC diagnostic billed at a different rate than general repairs."
          checked={cfg.shopOverrideService}
          onCheckedChange={(v) => update("shopOverrideService", v)}
        />
        {(cfg.shopOverrideTech || cfg.shopOverrideService) && (
          <Field
            label="Anything else we should know about your rate structure?"
            hint="optional"
          >
            <Textarea
              rows={3}
              placeholder="e.g. weekend rate for emergency calls, tiered rates by tech tenure, separate diagnostic vs. labor rates…"
              value={cfg.shopRateOverrideNotes}
              onChange={(e) => update("shopRateOverrideNotes", e.target.value)}
            />
          </Field>
        )}
      </div>
    </>
  );
}

// --- Step 7: Bill Rate -----------------------------------------------

function BillRateStep({ cfg, update }: StepProps) {
  const pct = effectiveMarkupPct(cfg);
  const isSample = !isUniversalPct(cfg);
  const computed = (cfg.shopRate * (1 + pct / 100)).toFixed(2);

  return (
    <>
      <StepHeader
        title="Bill rate"
        lede="What hourly rate do you bill for tech labor?"
      />
      <div className="space-y-5">
        <Field label="How is your bill rate set?">
          <RadioGroup
            value={cfg.billRateMode ?? ""}
            onValueChange={(v) => update("billRateMode", v as WizardConfig["billRateMode"])}
            className="grid gap-3 sm:grid-cols-2"
          >
            <RadioCard
              value="derived"
              title="Derived from shop rate × markup"
              help="The bill rate is calculated as a markup on the shop rate."
            />
            <RadioCard
              value="independent"
              title="Set independently"
              help="A flat bill rate not tied to the shop rate or markup."
            />
          </RadioGroup>
        </Field>

        {cfg.billRateMode === "derived" && (
          <div className="rounded-md bg-secondary px-4 py-3">
            <div className="font-medium tabular-nums">
              ${cfg.shopRate.toFixed(2)} × {(1 + pct / 100).toFixed(2)} = ${computed} / hr
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              {isSample
                ? `Illustrative — uses a sample ${pct}% since your markup structure isn't a single universal percentage.`
                : "Calculated from your shop rate and universal markup percentage."}
            </div>
          </div>
        )}

        {cfg.billRateMode === "independent" && (
          <Field label="Bill rate">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">$</span>
              <Input
                type="number"
                value={cfg.billRate}
                onChange={(e) => update("billRate", parseFloat(e.target.value) || 0)}
                className="w-32"
              />
              <span className="text-sm text-muted-foreground">per hour</span>
            </div>
            {cfg.billRate < cfg.shopRate && (
              <p className="mt-2 text-xs text-warning">Warning: bill rate is below shop rate.</p>
            )}
          </Field>
        )}
      </div>
    </>
  );
}

// --- Step 9: Accounts Receivable Branding ----------------------------

function ArBrandingStep({ cfg, update }: StepProps) {
  return (
    <>
      <StepHeader
        title="How do you want your Accounts Receivable invoices branded?"
        lede={'By default, Property Meld brands the AR invoices we send to your owners with your company branding already configured in Property Meld. If you’d prefer different branding — your own logo and colors, or different branding per owner / portfolio — pick "Custom branding" below and tell us how it varies.'}
      />
      <div className="space-y-4">
        <RadioGroup
          value={cfg.usesCustomArBranding === null ? "" : cfg.usesCustomArBranding ? "custom" : "default"}
          onValueChange={(v) => update("usesCustomArBranding", v === "custom")}
          className="grid gap-3 sm:grid-cols-2"
        >
          <RadioCard
            value="default"
            title="Use my company's default branding"
            help="Standard Property Meld look for every AR invoice. Recommended for most PMs."
          />
          <RadioCard
            value="custom"
            title="Custom branding"
            help="Your own logo / colors, or different branding per owner or portfolio."
          />
        </RadioGroup>
        {cfg.usesCustomArBranding && (
          <Field
            label="How does your branding vary?"
            hint="optional — by owner, by portfolio, anything else"
          >
            <Textarea
              rows={3}
              value={cfg.arBrandingNotes}
              onChange={(e) => update("arBrandingNotes", e.target.value)}
              placeholder="e.g. our own logo + cyan/gray colors across the board; or different logo for the luxury portfolio; or per-owner branding for our top 3 institutional clients…"
            />
          </Field>
        )}
      </div>
    </>
  );
}

// --- Step 10: Additional Notes ---------------------------------------

function AdditionalNotesStep({ cfg, update }: StepProps) {
  return (
    <>
      <StepHeader
        title="Anything else we should know?"
        lede="Open mic — share any color on your billing process, edge cases we haven't asked about, or features you'd want from TrueCost. All optional."
      />
      <Field label="Additional comments" hint="optional">
        <Textarea
          rows={6}
          value={cfg.additionalNotes}
          onChange={(e) => update("additionalNotes", e.target.value)}
          placeholder="e.g. seasonal pricing, escrow handling, owner-specific quirks, integrations you wish you had…"
        />
      </Field>
    </>
  );
}

// --- Step 10: Preview -------------------------------------------------

function PreviewStep({ cfg }: { cfg: WizardConfig }) {
  const showsMarkupHere = showsMarkup(cfg);
  const vendorInvoice = 480;
  const pct = showsMarkupHere && isUniversalPct(cfg) ? cfg.markupValue : 0;
  const flatFee = showsMarkupHere && isUniversalFlat(cfg) ? cfg.markupValue : 0;
  const markupAmt = pct ? Math.round(vendorInvoice * pct) / 100 : 0;
  const techHours = 2.5;
  const laborCost = Math.round(cfg.shopRate * techHours * 100) / 100;
  const billRate = effectiveBillRate(cfg);
  const laborCharge = Math.round(billRate * techHours * 100) / 100;
  const total = Math.round((vendorInvoice + markupAmt + flatFee + laborCharge) * 100) / 100;
  const fmt = (n: number) => `$${n.toFixed(2)}`;

  const showAr = cfg.businessModel !== "owned";
  const corpName = corporateLabel(cfg);
  const corpAvailable = cfg.corporate !== "Same as PAS"; // collapses into PAS

  function deliveryParts(destPas: boolean, destCorp: boolean, email: string): string[] {
    const parts: string[] = [];
    if (destPas && cfg.pas && cfg.pas !== "None") parts.push(`PAS push to ${cfg.pas}`);
    if (destCorp && corpAvailable) {
      parts.push(email ? `PDF emailed to ${corpName} (${email})` : `PDF emailed to ${corpName}`);
    } else if (email) {
      parts.push(`PDF emailed to ${email}`);
    }
    return parts;
  }

  const apParts = deliveryParts(cfg.apDestPas, cfg.apDestCorporate, cfg.apEmail);
  const arParts = showAr ? deliveryParts(cfg.arDestPas, cfg.arDestCorporate, cfg.arEmail) : [];

  return (
    <>
      <StepHeader
        title="Sample work order with your answers"
        lede="If we modeled your billing on a typical HVAC service call, here's what it would look like. Helps us sanity-check that we captured your structure correctly."
      />
      <div className="rounded-lg bg-secondary p-4 sm:p-5">
        <div className="flex flex-col gap-1 border-b border-border pb-3 sm:flex-row sm:items-baseline sm:justify-between">
          <div className="font-medium">HVAC service call — 123 Maple St</div>
          <div className="text-xs text-muted-foreground">Sample work order</div>
        </div>
        <PreviewRow label="Vendor invoice (ACME HVAC)" value={fmt(vendorInvoice)} />
        {markupAmt > 0 && <PreviewRow label={`Markup (${pct}%)`} value={fmt(markupAmt)} />}
        {flatFee > 0 && <PreviewRow label="Flat fee per meld" value={fmt(flatFee)} />}
        <PreviewRow
          label={`Tech labor — ${techHours} hrs @ ${fmt(billRate)}/hr`}
          value={fmt(laborCharge)}
        />
        <div className="mt-2 flex justify-between border-t border-border pt-3 font-medium">
          <span>Total billed</span>
          <span className="tabular-nums">{fmt(total)}</span>
        </div>

        <div className="mt-5 border-t border-border pt-4">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Delivery
          </div>
          <ul className="space-y-1.5 text-sm">
            <li>
              <span className="text-muted-foreground">AP bill →</span>{" "}
              <span>{apParts.length ? apParts.join(", ") : "TrueCost only"}</span>
            </li>
            {showAr && (
              <li>
                <span className="text-muted-foreground">AR invoice →</span>{" "}
                <span>{arParts.length ? arParts.join(", ") : "TrueCost only"}</span>
              </li>
            )}
            {cfg.pas && cfg.pas !== "None" && cfg.pmsSyncMode && (
              <li>
                <span className="text-muted-foreground">PAS sync →</span>{" "}
                <span>{syncLabelFor(cfg.pas, cfg.pmsSyncMode)}</span>
              </li>
            )}
          </ul>
          <p className="mt-3 text-xs italic text-muted-foreground">
            AP and AR PDFs are always available for download in TrueCost.
          </p>
        </div>

        <div className="mt-3 text-xs text-muted-foreground">
          Internal labor cost (shop × hrs): {fmt(laborCost)} · Margin on labor: {fmt(laborCharge - laborCost)}
        </div>
      </div>
    </>
  );
}

function PreviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between py-1.5 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}

// --- Step 11: Review --------------------------------------------------

function ReviewStep({ cfg }: { cfg: WizardConfig }) {
  const showAr = cfg.businessModel !== "owned";
  const dash = "—";
  const billRateShown =
    cfg.billRateMode === "derived"     ? `$${effectiveBillRate(cfg).toFixed(2)} / hr (derived)` :
    cfg.billRateMode === "independent" ? `$${cfg.billRate.toFixed(2)} / hr` :
    dash;

  const businessModelLabel = cfg.businessModel === null
    ? dash
    : (BUSINESS_MODEL_OPTIONS.find((o) => o.value === cfg.businessModel)?.title ?? cfg.businessModel);

  const apDest = [cfg.apDestPas && "PAS", cfg.apDestCorporate && "Corporate"].filter(Boolean).join(" + ") || "TrueCost only";
  const arDest = showAr ? ([cfg.arDestPas && "PAS", cfg.arDestCorporate && "Corporate"].filter(Boolean).join(" + ") || "TrueCost only") : null;

  const blocks: { title: string; rows: [string, string][] }[] = [];

  blocks.push({
    title: "Business model",
    rows: [["Model", businessModelLabel]],
  });

  const corpDisplay =
    cfg.corporate === null ? dash :
    cfg.corporate === "Other" ? `Other${cfg.corporateOther ? ` — ${cfg.corporateOther}` : ""}` :
    cfg.corporate;
  blocks.push({
    title: "Systems",
    rows: [
      ["PAS", cfg.pas ?? dash],
      ["Corporate accounting", corpDisplay],
    ],
  });

  const routingRows: [string, string][] = [];
  routingRows.push(["AP destination", apDest]);
  if (cfg.apDestCorporate) {
    routingRows.push(["AP corporate email", cfg.apEmail || dash]);
  }
  if (showAr && arDest) {
    routingRows.push(["AR destination", arDest]);
    if (cfg.arDestCorporate) {
      routingRows.push(["AR corporate email", cfg.arEmail || dash]);
    }
  }
  if (cfg.pas && cfg.pas !== "None") {
    routingRows.push([
      "PMS sync mode",
      cfg.pmsSyncMode ? syncLabelFor(cfg.pas, cfg.pmsSyncMode) : dash,
    ]);
  }
  blocks.push({ title: "AP / AR routing", rows: routingRows });

  if (showAr) {
    const contractTitle = cfg.contractModel === null
      ? dash
      : (CONTRACT_MODEL_OPTIONS.find((o) => o.value === cfg.contractModel)?.title ?? cfg.contractModel);
    blocks.push({ title: "Contract model", rows: [["Model", contractTitle]] });

    if (showsMarkup(cfg)) {
      const kindLabel = cfg.markupKind === null
        ? dash
        : (MARKUP_KIND_OPTIONS.find((o) => o.value === cfg.markupKind)?.title ?? cfg.markupKind);
      const basisLabel = cfg.markupBasis === null
        ? dash
        : (MARKUP_BASIS_OPTIONS.find((o) => o.value === cfg.markupBasis)?.title ?? cfg.markupBasis);
      const markupRows: [string, string][] = [["Kind", kindLabel]];
      if (cfg.markupKind === "other") {
        markupRows.push(["Description", cfg.markupOther || "(unspecified)"]);
      } else if (cfg.markupKind !== null) {
        markupRows.push(["Applied", basisLabel]);
        if (isUniversalFlat(cfg)) {
          markupRows.push(["Typical fee", `$${cfg.markupValue.toFixed(2)} / meld`]);
        } else if (isUniversalPct(cfg)) {
          markupRows.push(["Typical %", `${cfg.markupValue.toFixed(0)}%`]);
        }
      }
      blocks.push({ title: "Markup application", rows: markupRows });
    }
  }

  const rateRows: [string, string][] = [
    ["Shop rate", `$${cfg.shopRate.toFixed(2)} / hr`],
    ["Bill rate", billRateShown],
    ["Different rates per technician", cfg.shopOverrideTech ? "Yes" : "No"],
    ["Different rates per service category", cfg.shopOverrideService ? "Yes" : "No"],
  ];
  if ((cfg.shopOverrideTech || cfg.shopOverrideService) && cfg.shopRateOverrideNotes.trim()) {
    rateRows.push(["Notes", cfg.shopRateOverrideNotes.trim()]);
  }
  blocks.push({ title: "Rates", rows: rateRows });

  const brandingRows: [string, string][] = [
    ["AR invoice branding",
      cfg.usesCustomArBranding === null ? dash :
      cfg.usesCustomArBranding ? "Custom" : "Property Meld default"],
  ];
  if (cfg.usesCustomArBranding && cfg.arBrandingNotes.trim()) {
    brandingRows.push(["Notes", cfg.arBrandingNotes.trim()]);
  }
  blocks.push({ title: "Accounts Receivable branding", rows: brandingRows });

  if (cfg.additionalNotes.trim()) {
    blocks.push({ title: "Additional notes", rows: [["", cfg.additionalNotes.trim()]] });
  }

  return (
    <>
      <StepHeader
        title="Review your responses"
        lede="Last look before submitting. We'll save these answers and produce a few sample documents based on what you told us."
      />
      <div className="divide-y divide-border">
        {blocks.map((b) => (
          <div key={b.title} className="py-3">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {b.title}
            </div>
            <dl className="space-y-1">
              {b.rows.map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 text-sm">
                  <dt className="text-muted-foreground">{k}</dt>
                  <dd className="text-right font-medium">{v}</dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
      </div>
      <p className="mt-4 text-xs text-muted-foreground">
        Click <span className="font-medium text-foreground">Submit responses</span> to send. The
        next screen has sample AP / AR documents we'd produce based on your answers — please
        review and let us know if anything looks off.
      </p>
    </>
  );
}
